/**
 * AgentStatusDashboard Component
 * 
 * Displays real-time agent status for CEO/HOD dashboards:
 * - 🟢 GREEN = Agent inside office geofence
 * - 🔴 RED = Agent outside office geofence but app active
 * - 🔵 BLUE = Agent on approved leave
 * 
 * NO OFFLINE STATUS - every agent shows green, red, or blue during office hours.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import HelpInfo from '../components/HelpInfo';
import { 
  Dialog,
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger 
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  MapPin,
  Users,
  Building2,
  Plane,
  AlertTriangle,
  RefreshCw,
  Settings,
  Plus,
  Trash2,
  Filter
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Status color mapping
const STATUS_COLORS = {
  inside: { bg: 'bg-green-500', text: 'text-green-500', label: 'In Office', dot: '🟢' },
  outside: { bg: 'bg-red-500', text: 'text-red-500', label: 'Outside', dot: '🔴' },
  on_leave: { bg: 'bg-blue-500', text: 'text-blue-500', label: 'On Leave', dot: '🔵' }
};

// `departmentFilter` (optional): when set to 'sales' or 'marketing',
// the agent list, summary tiles, and stats only count rows whose role
// or department matches. Used by the HOD Dashboard so the top-right
// dropdown ("Sales / Marketing / All") controls this panel too.
const matchesDept = (agent, departmentFilter) => {
  if (!departmentFilter || departmentFilter === 'all') return true;
  const role = (agent?.role || agent?.user_role || '').toLowerCase();
  const dept = (agent?.department || '').toLowerCase();
  if (departmentFilter === 'sales') {
    return role.includes('sales') || role === 'cr_agent' || dept.includes('sales');
  }
  if (departmentFilter === 'marketing') {
    return role.includes('marketing') || dept.includes('marketing');
  }
  return true;
};

const AgentStatusDashboard = ({ compact = false, departmentFilter = 'all' }) => {
  const { token } = useAuth();
  const [agents, setAgents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null); // 'inside', 'outside', 'on_leave', or null for all
  const [geofences, setGeofences] = useState([]);
  const [officeSettings, setOfficeSettings] = useState(null);
  const [showGeofenceDialog, setShowGeofenceDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [newGeofence, setNewGeofence] = useState({ name: '', center_lat: '', center_lng: '', radius_meters: 200 });
  const [newSettings, setNewSettings] = useState({ office_hours_start: '', office_hours_end: '' });

  const fetchAgentStatuses = useCallback(async () => {
    if (!token) return;
    
    try {
      const params = new URLSearchParams({ token });
      if (statusFilter) params.append('status_filter', statusFilter);
      
      const response = await fetch(`${API_BASE}/api/agent-status/all?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setAgents(data.agents || []);
      }
    } catch (error) {
      console.error('Error fetching agent statuses:', error);
    }
  }, [token, statusFilter]);

  const fetchSummary = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/agent-status/summary?token=${token}`);
      const data = await response.json();
      
      if (data.success) {
        setSummary(data.summary);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [token]);

  const fetchGeofences = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/agent-status/geofences?token=${token}`);
      const data = await response.json();
      
      if (data.success) {
        setGeofences(data.geofences || []);
      }
    } catch (error) {
      console.error('Error fetching geofences:', error);
    }
  }, [token]);

  const fetchOfficeSettings = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/agent-status/office-settings?token=${token}`);
      const data = await response.json();
      
      if (data.success) {
        setOfficeSettings(data.settings);
        setNewSettings({
          office_hours_start: data.settings.office_hours_start || '08:00',
          office_hours_end: data.settings.office_hours_end || '22:00'
        });
      }
    } catch (error) {
      console.error('Error fetching office settings:', error);
    }
  }, [token]);

  const createGeofence = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/agent-status/geofences?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newGeofence.name,
          center_lat: parseFloat(newGeofence.center_lat),
          center_lng: parseFloat(newGeofence.center_lng),
          radius_meters: parseInt(newGeofence.radius_meters)
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Geofence created successfully');
        setShowGeofenceDialog(false);
        setNewGeofence({ name: '', center_lat: '', center_lng: '', radius_meters: 200 });
        fetchGeofences();
      } else {
        toast.error(data.detail || 'Failed to create geofence');
      }
    } catch (error) {
      toast.error('Error creating geofence');
    }
  };

  const deleteGeofence = async (geofenceId) => {
    try {
      const response = await fetch(`${API_BASE}/api/agent-status/geofences/${geofenceId}?token=${token}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Geofence deleted');
        fetchGeofences();
      } else {
        toast.error(data.detail || 'Failed to delete geofence');
      }
    } catch (error) {
      toast.error('Error deleting geofence');
    }
  };

  const updateOfficeSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/agent-status/office-settings?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSettings)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Office hours updated');
        setShowSettingsDialog(false);
        fetchOfficeSettings();
      } else {
        toast.error(data.detail || 'Failed to update settings');
      }
    } catch (error) {
      toast.error('Error updating settings');
    }
  };

  useEffect(() => {
    // Don't block the whole card on the slowest endpoint. Mark loading
    // false as soon as the primary agent feed lands — geofences /
    // office-settings can stream in afterwards. Halves perceived load
    // time on slower networks.
    setLoading(true);
    fetchAgentStatuses().finally(() => setLoading(false));
    fetchSummary();
    fetchGeofences();
    fetchOfficeSettings();

    const interval = setInterval(() => {
      fetchAgentStatuses();
      fetchSummary();
    }, 60000);

    return () => clearInterval(interval);
  }, [fetchAgentStatuses, fetchSummary, fetchGeofences, fetchOfficeSettings]);

  // Filter buttons for status
  const FilterButtons = () => (
    <div className="flex gap-2 flex-wrap">
      <Button 
        size="sm" 
        variant={statusFilter === null ? "default" : "outline"}
        onClick={() => setStatusFilter(null)}
      >
        All ({summary?.total_agents || 0})
      </Button>
      <Button 
        size="sm" 
        variant={statusFilter === 'inside' ? "default" : "outline"}
        onClick={() => setStatusFilter('inside')}
        className="text-green-600"
      >
        <div className="w-2 h-2 rounded-full bg-green-500 mr-2" />
        Inside ({summary?.inside_office || 0})
      </Button>
      <Button 
        size="sm" 
        variant={statusFilter === 'outside' ? "default" : "outline"}
        onClick={() => setStatusFilter('outside')}
        className="text-red-600"
      >
        <div className="w-2 h-2 rounded-full bg-red-500 mr-2" />
        Outside ({summary?.outside_office || 0})
      </Button>
      <Button 
        size="sm" 
        variant={statusFilter === 'on_leave' ? "default" : "outline"}
        onClick={() => setStatusFilter('on_leave')}
        className="text-blue-600"
      >
        <div className="w-2 h-2 rounded-full bg-blue-500 mr-2" />
        On Leave ({summary?.on_leave || 0})
      </Button>
    </div>
  );

  // Summary cards
  const SummaryCards = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Agents</p>
              <p className="text-2xl font-bold">{summary?.total_agents || 0}</p>
            </div>
            <Users className="h-8 w-8 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">In Office</p>
              <p className="text-2xl font-bold text-green-600">{summary?.inside_office || 0}</p>
            </div>
            <Building2 className="h-8 w-8 text-green-500" />
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Outside Office</p>
              <p className="text-2xl font-bold text-red-600">{summary?.outside_office || 0}</p>
            </div>
            <MapPin className="h-8 w-8 text-red-500" />
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">On Leave</p>
              <p className="text-2xl font-bold text-blue-600">{summary?.on_leave || 0}</p>
            </div>
            <Plane className="h-8 w-8 text-blue-500" />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Agent list item
  const AgentListItem = ({ agent }) => {
    const statusConfig = STATUS_COLORS[agent.status] || STATUS_COLORS.outside;
    
    return (
      <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Avatar className="h-10 w-10">
              <AvatarImage src={agent.picture} alt={agent.name} />
              <AvatarFallback>{agent.name?.charAt(0) || '?'}</AvatarFallback>
            </Avatar>
            {/* Status dot */}
            <div 
              className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ${statusConfig.bg} border-2 border-background flex items-center justify-center`}
            >
              {agent.gps_warning && (
                <AlertTriangle className="w-2.5 h-2.5 text-white" />
              )}
            </div>
          </div>
          
          <div>
            <p className="font-medium">{agent.name}</p>
            <p className="text-sm text-muted-foreground">{agent.email}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={statusConfig.text}>
            {statusConfig.dot} {statusConfig.label}
          </Badge>
          
          {agent.gps_warning && (
            <Badge variant="destructive" className="text-xs">
              <AlertTriangle className="w-3 h-3 mr-1" />
              GPS Stale
            </Badge>
          )}
          
          {agent.agent_type && (
            <Badge variant="secondary" className="text-xs capitalize">
              {agent.agent_type}
            </Badge>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading agent statuses...</span>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    // Compact view for sidebar/widgets - aligned with ResponseTimeCard
    return (
      <Card className="h-full" data-testid="agent-status-card-compact">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2 flex-wrap">
              <Users className="h-4 w-4" />
              Agent Status
              <HelpInfo
                align="inline"
                title="How to read Agent Status"
                steps={[
                  <>Every team member shows one of three live states: <strong className="text-emerald-600">In Office</strong> (inside the office geofence), <strong className="text-rose-600">In Field</strong> (app active but outside the office), or <strong className="text-blue-600">On Leave</strong> (approved leave today).</>,
                  'The three tiles at the top are clickable — tap one to filter the list to just that status.',
                  'There is no "offline" state during office hours: an agent is always green, red, or blue, so a missing colour means their app isn\'t reporting — worth a check.',
                  'Click the refresh icon to pull the latest, or just wait — the panel updates on its own.',
                ]}
                tip="A long In Field list early in the day is healthy. The same list late afternoon with no site-visit reports filed usually means agents forgot to close out."
              />
            </span>
            <Button size="icon" variant="ghost" onClick={() => { fetchAgentStatuses(); fetchSummary(); }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Summary row — three clickable tiles. When the parent passes
              a `departmentFilter`, we compute counts locally from the
              filtered agent list so the tiles match what's actually
              shown below; otherwise we use the cheaper global summary
              from the /summary endpoint. */}
          {(() => {
            const useLocal = departmentFilter && departmentFilter !== 'all';
            const localCounts = (() => {
              if (!useLocal) return null;
              const inDept = agents.filter((a) => matchesDept(a, departmentFilter));
              const inStatus = (s) => inDept.filter((a) => a.status === s).length;
              return {
                inside: inStatus('inside') + inStatus('in_office'),
                outside: inStatus('outside') + inStatus('in_field'),
                on_leave: inStatus('on_leave'),
              };
            })();
            const tileValue = (k) => useLocal
              ? (localCounts ? localCounts[k] : 0)
              : (k === 'inside' ? summary?.inside_office
                : k === 'outside' ? summary?.outside_office
                : summary?.on_leave) || 0;
            return (
          <div className="grid grid-cols-3 gap-2 mb-4 text-center">
            {[
              { k: 'inside',   label: 'In Office', value: tileValue('inside'),
                bg: 'bg-green-50', border: 'border-green-200', active: 'ring-green-500 border-green-500', text: 'text-green-700', dot: 'bg-green-500' },
              { k: 'outside',  label: 'In Field',  value: tileValue('outside'),
                bg: 'bg-red-50',   border: 'border-red-200',   active: 'ring-red-500 border-red-500',     text: 'text-red-700',   dot: 'bg-red-500' },
              { k: 'on_leave', label: 'On Leave',  value: tileValue('on_leave'),
                bg: 'bg-blue-50',  border: 'border-blue-200',  active: 'ring-blue-500 border-blue-500',   text: 'text-blue-700',  dot: 'bg-blue-500' },
            ].map((tile) => (
              <button
                key={tile.k}
                type="button"
                onClick={() => setStatusFilter(statusFilter === tile.k ? null : tile.k)}
                className={`rounded-md border py-2 text-center transition ${tile.bg} ${
                  statusFilter === tile.k ? `ring-2 ${tile.active}` : tile.border + ' hover:opacity-90'
                }`}
              >
                <div className={`flex items-center justify-center gap-1.5 ${tile.text}`}>
                  <span className={`w-2.5 h-2.5 rounded-full ${tile.dot}`} />
                  <span className="text-base font-semibold">{tile.value}</span>
                </div>
                <div className={`text-[11px] ${tile.text}`}>{tile.label}</div>
              </button>
            ))}
          </div>
            );
          })()}

          {/* Banner explaining the active filter + a clear button. */}
          {statusFilter && (
            <div className="flex items-center justify-between text-xs mb-2 px-2 py-1 rounded bg-slate-50 border border-slate-200">
              <span>
                Filter: <strong>{statusFilter === 'inside' ? 'In Office' : statusFilter === 'outside' ? 'In Field' : 'On Leave'}</strong>
              </span>
              <button onClick={() => setStatusFilter(null)} className="text-slate-500 hover:text-slate-700 underline">
                Clear
              </button>
            </div>
          )}

          {/* Agent list — filtered by the active tile when set, AND by
              the department dropdown from the parent (Sales / Marketing /
              All). Scrolls vertically so the full roster is accessible
              without leaving the card. */}
          <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
            {agents
              .filter((a) => matchesDept(a, departmentFilter))
              .filter((a) => !statusFilter || a.status === statusFilter)
              .map((agent) => {
              const label =
                agent.status === 'inside' || agent.status === 'in_office' ? 'In Office'
                : agent.status === 'outside' || agent.status === 'in_field' ? 'In Field'
                : agent.status === 'on_leave' ? 'On Leave'
                : 'Unknown';
              const colors = STATUS_COLORS[agent.status] || { bg: 'bg-gray-400', text: 'text-gray-500' };
              return (
                <div key={agent.agent_id} className="flex items-center justify-between text-sm py-0.5">
                  <span className="truncate max-w-[150px] font-medium">{agent.name}</span>
                  <span className={`flex items-center gap-1.5 text-xs ${colors.text}`}>
                    <span className={`w-2 h-2 rounded-full ${colors.bg}`} />
                    {label}
                  </span>
                </div>
              );
            })}
            {(() => {
              const filtered = agents
                .filter((a) => matchesDept(a, departmentFilter))
                .filter((a) => !statusFilter || a.status === statusFilter);
              if (filtered.length === 0) {
                return (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    {statusFilter ? 'No agents in this state.' : 'No agents found'}
                  </p>
                );
              }
              return null;
            })()}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full dashboard view
  return (
    <div className="space-y-6" data-testid="agent-status-dashboard">
      {/* Header with settings */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Agent Status Dashboard</h2>
          {officeSettings && (
            <p className="text-sm text-muted-foreground">
              Office Hours: {officeSettings.office_hours_start} - {officeSettings.office_hours_end}
            </p>
          )}
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchAgentStatuses(); fetchSummary(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          
          <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Office Hours
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Office Hours Settings</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Start Time</Label>
                  <Input 
                    type="time" 
                    value={newSettings.office_hours_start}
                    onChange={(e) => setNewSettings({ ...newSettings, office_hours_start: e.target.value })}
                  />
                </div>
                <div>
                  <Label>End Time</Label>
                  <Input 
                    type="time" 
                    value={newSettings.office_hours_end}
                    onChange={(e) => setNewSettings({ ...newSettings, office_hours_end: e.target.value })}
                  />
                </div>
                <Button onClick={updateOfficeSettings} className="w-full">
                  Save Settings
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={showGeofenceDialog} onOpenChange={setShowGeofenceDialog}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Geofence
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Office Geofence</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input 
                    placeholder="e.g., Dubai Head Office"
                    value={newGeofence.name}
                    onChange={(e) => setNewGeofence({ ...newGeofence, name: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Latitude</Label>
                    <Input 
                      type="number" 
                      step="any"
                      placeholder="e.g., 25.2048"
                      value={newGeofence.center_lat}
                      onChange={(e) => setNewGeofence({ ...newGeofence, center_lat: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Longitude</Label>
                    <Input 
                      type="number"
                      step="any"
                      placeholder="e.g., 55.2708"
                      value={newGeofence.center_lng}
                      onChange={(e) => setNewGeofence({ ...newGeofence, center_lng: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Radius (meters)</Label>
                  <Input 
                    type="number"
                    value={newGeofence.radius_meters}
                    onChange={(e) => setNewGeofence({ ...newGeofence, radius_meters: e.target.value })}
                  />
                </div>
                <Button onClick={createGeofence} className="w-full">
                  Create Geofence
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary cards */}
      <SummaryCards />

      {/* Geofences section */}
      {geofences.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Office Geofences</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {geofences.map(gf => (
                <Badge key={gf.id} variant="secondary" className="py-1 px-3">
                  <MapPin className="w-3 h-3 mr-1" />
                  {gf.name} ({gf.radius_meters}m)
                  <button 
                    onClick={() => deleteGeofence(gf.id)}
                    className="ml-2 hover:text-destructive"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter and agent list */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filter Agents
            </CardTitle>
          </div>
          <FilterButtons />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {agents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No agents found with the selected filter
              </div>
            ) : (
              agents.map(agent => (
                <AgentListItem key={agent.agent_id} agent={agent} />
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentStatusDashboard;
